import { addDays, startOfWeek, startOfMonth, endOfMonth, isSameDay } from "date-fns";
import type { CalendarEvent } from "@/components/calendar/views/CalendarEventTypes";

const DAY_START_MIN = 8 * 60;  // 08:00
const DAY_END_MIN   = 19 * 60; // end of the 18:00 row
const MIN_BLOCK_PX = 28;

const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

export function dayKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function minutesOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

export function eventColor(event: CalendarEvent): string {
  return event.clinic_appointment_type?.color ?? "#75716a";
}

export function groupEventsByDay(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>();
  for (const event of events) {
    const key = dayKey(new Date(event.start));
    const bucket = map.get(key);
    if (bucket) bucket.push(event);
    else map.set(key, [event]);
  }
  for (const bucket of map.values()) {
    bucket.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  }
  return map;
}

export function eventsForDay(date: Date, grouped: Map<string, CalendarEvent[]>): CalendarEvent[] {
  return grouped.get(dayKey(date)) ?? [];
}

export type MonthCell = {
  date: Date;
  day: number;
  faded: boolean;
  selected: boolean;
  isToday: boolean;
  dotColors: string[];
};

export function buildMonthCells(
  selected: Date,
  grouped: Map<string, CalendarEvent[]>,
  today: Date,
): MonthCell[] {
  const gridStart = startOfWeek(startOfMonth(selected), { weekStartsOn: 0 });
  const monthStart = startOfMonth(selected);
  const monthEnd = endOfMonth(selected);
  const cells: MonthCell[] = [];
  for (let i = 0; i < 42; i++) {
    const date = addDays(gridStart, i);
    const faded = date < monthStart || date > monthEnd;
    const dayEvents = faded ? [] : eventsForDay(date, grouped);
    cells.push({
      date,
      day: date.getDate(),
      faded,
      selected: !faded && isSameDay(date, selected),
      isToday: !faded && isSameDay(date, today),
      dotColors: dayEvents.slice(0, 3).map(eventColor),
    });
  }
  return cells;
}

export type WeekDay = {
  date: Date;
  weekdayLabel: string;
  day: number;
  selected: boolean;
  hasEvents: boolean;
  eventCount: number;
};

const WEEKDAY_SHORT = ["D", "S", "T", "Q", "Q", "S", "S"];

export function buildWeekStrip(selected: Date, grouped: Map<string, CalendarEvent[]>): WeekDay[] {
  const start = startOfWeek(selected, { weekStartsOn: 0 });
  const out: WeekDay[] = [];
  for (let i = 0; i < 7; i++) {
    const date = addDays(start, i);
    const eventCount = eventsForDay(date, grouped).length;
    out.push({
      date,
      weekdayLabel: WEEKDAY_SHORT[i],
      day: date.getDate(),
      selected: isSameDay(date, selected),
      hasEvents: eventCount > 0,
      eventCount,
    });
  }
  return out;
}

export type TimelineBlock = {
  event: CalendarEvent;
  topPx: number;
  heightPx: number;
  color: string;
  isEarly: boolean;
  isLate: boolean;
};

export function buildDayTimeline(date: Date, grouped: Map<string, CalendarEvent[]>): TimelineBlock[] {
  return eventsForDay(date, grouped).map((event) => {
    const startMin = minutesOfDay(new Date(event.start));
    const endMin = minutesOfDay(new Date(event.end));
    const duration = Math.max(endMin - startMin, 0);
    const isEarly = startMin < DAY_START_MIN;
    const isLate = !isEarly && startMin >= DAY_END_MIN;
    return {
      event,
      topPx: Math.max(0, startMin - DAY_START_MIN),
      heightPx: isEarly || isLate ? MIN_BLOCK_PX : Math.max(duration, MIN_BLOCK_PX),
      color: eventColor(event),
      isEarly,
      isLate,
    };
  });
}

export const TIMELINE_HOURS = ["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00"];
